// ==========================
// Version 43 — MemoryDetails.js (just-in-time public mint disclosure)
// - Keeps the 4-step upload → verify → mint wizard
// - Keeps resumable upload/verify support
// - Removes temporary debug logs and map debug panel
// ==========================
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
// REMOVED: import html2canvas from 'html2canvas';
import { serverTimestamp } from 'firebase/firestore';
import { getMemoryById, createFlag, saveMemoryNftMint, saveMemoryNftPending, updateMemoryDoc } from '../db';
import { MAPS_LOADER_OPTIONS, withBrandMapOptions } from '../mapsConfig';
import { toast } from '../utils/toast';
import { useAuth } from '../context/AuthContext';
import Seo from '../components/Seo';
import { uploadMemoryNftToIpfs } from '../nft/mintWithPinata';
import { verifyUploadedNftAssets } from '../nft/verifyUploadedNftAssets';
// Mint process
import { useActiveAccount } from 'thirdweb/react';
import { mintMemoryOnChain } from '../nft/mintOnChain';
import { NFT_COLLECTION_ADDRESS } from '../thirdweb.config';
import { buildMintRecord, getMemoryNftLatest, isMemoryMarkedMinted, resolveMemoryMintStatus } from '../nft/status';
import { buildMintDisclosureItems } from '../nft/mintDisclosure';
import { useLinkedWallet } from '../wallet/useLinkedWallet';
import { useWalletReservation } from '../wallet/useWalletReservation';
import { walletMatchesLinkedAddress } from '../wallet/walletLinking';

const NAVY = '#0A1A33';
const GOLD = '#C9A44C';
const IVORY = '#FDFCF9';
const WARM_GRAY = '#888888';
const ACCESSIBLE_GOLD = '#8A6D2E';
const BURGUNDY = '#5C1A2B';
const ROSE_CLAY = '#B76E79';
const TEAL = '#0F766E';
const EMERALD = '#0F6E56';

const mapStyle = { width: '100%', height: 260, borderRadius: 12, marginTop: 12 };

// --- html2canvas dynamic loader (code-split) ---
let _h2cPromise;
async function getHtml2Canvas() {
  if (!_h2cPromise) {
    _h2cPromise = import('html2canvas').then((m) => m.default || m);
  }
  return _h2cPromise;
}

// SVG drop-pin helper (kept for future use; current map uses SymbolPath)
function pinUrl(
  { body = NAVY, ring = GOLD, highlight = '#ffffff', shadow = 'rgba(0,0,0,0.25)' } = {}
) {
  const svg = `
  <svg xmlns="http://www.w3.org/2000/svg" width="44" height="56" viewBox="0 0 44 56">
    <ellipse cx="22" cy="52" rx="10" ry="4" fill="${shadow}"/>
    <path d="M22 2c10 0 18 7.8 18 17.4C40 33.4 22 54 22 54S4 33.4 4 19.4C4 9.8 12 2 22 2z" fill="${body}"/>
    <path d="M16 7c-5 3.5-8 9-8 12.9C8 31 22 47 22 47V5.5C19.5 5.8 17.6 6.4 16 7z" fill="${highlight}" opacity="0.10"/>
    <circle cx="22" cy="19" r="7.5" fill="none" stroke="${ring}" stroke-width="2.5"/>
    <circle cx="22" cy="19" r="4.2" fill="${ring}"/>
  </svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

function shortAddr(addr) {
  if (!addr) return '';
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

export default function MemoryDetails() {
  const { id } = useParams();
  const locationState = useLocation().state;
  const { currentUser } = useAuth();
  const { loading: linkedWalletLoading, wallet: linkedWallet } = useLinkedWallet(currentUser?.uid);

  // Mint process
  const account = useActiveAccount();
  const walletAddress = account?.address || null;
  const {
    loading: walletReservationLoading,
    reservation: walletReservation,
  } = useWalletReservation(walletAddress);

  const [memory, setMemory] = useState(locationState?.memory || null);
  const [status, setStatus] = useState('loading');

  const exportRef = useRef(null);
  const visibleCardRef = useRef(null);
  const copyHelperRef = useRef(null);
  const shareBtnRef = useRef(null);
  const { isLoaded } = useJsApiLoader(MAPS_LOADER_OPTIONS);

  const [copied, setCopied] = useState(false);
  const [liveMsg, setLiveMsg] = useState('');
  const [shareOpen, setShareOpen] = useState(false);

  // Report modal state
  const [reportOpen, setReportOpen] = useState(false);
  const [reason, setReason] = useState('spam');
  const [note, setNote] = useState('');
  const [reportBusy, setReportBusy] = useState(false);

  // Focus management for the report dialog
  const reportTriggerRef = useRef(null); // the "Report" button
  const reportFirstControlRef = useRef(null); // first radio in dialog
  const closeBtnRef = useRef(null); // Cancel button in dialog
  const prevReportOpenRef = useRef(false);

  // ==========================
  // Immortalize Wizard
  // ==========================
  const [immOpen, setImmOpen] = useState(false);
  const [immStep, setImmStep] = useState(1); // 1 confirm, 2 upload, 3 verify, 4 mint
  const [immUploadBusy, setImmUploadBusy] = useState(false);
  const [immUploadDone, setImmUploadDone] = useState(false);
  const [immUploadError, setImmUploadError] = useState('');
  const [immUploadResult, setImmUploadResult] = useState(null); // { tokenUri, imageCid, metadataCid, metadata, gatewayUrl }
  const [immPublishBusy, setImmPublishBusy] = useState(false);
  const [immPublishError, setImmPublishError] = useState('');

  const [immVerifyBusy, setImmVerifyBusy] = useState(false);
  const [immVerifyDone, setImmVerifyDone] = useState(false);
  const [immVerifyError, setImmVerifyError] = useState('');
  const [immVerifyAttempt, setImmVerifyAttempt] = useState(0);
  const [immVerifyMessage, setImmVerifyMessage] = useState('');
  const [immVerifyResult, setImmVerifyResult] = useState(null);

  const [immMintBusy, setImmMintBusy] = useState(false);
  const [immMintAttempted, setImmMintAttempted] = useState(false);
  const [immMintError, setImmMintError] = useState('');
  const [immMintResult, setImmMintResult] = useState(null); // { txHash, tokenId, explorerUrl }
  const [immDisclosureAccepted, setImmDisclosureAccepted] = useState(false);
  const [mintPersistBusy, setMintPersistBusy] = useState(false);
  const [mintStatus, setMintStatus] = useState({
    isMinted: false,
    latest: null,
    source: 'none',
    chainConfirmed: false,
  });

  const immFirstBtnRef = useRef(null);

  const closeImmModal = () => {
    setImmOpen(false);
    setImmStep(1);
    setImmUploadBusy(false);
    setImmUploadDone(false);
    setImmUploadError('');
    setImmUploadResult(null);
    setImmPublishBusy(false);
    setImmPublishError('');
    setImmVerifyBusy(false);
    setImmVerifyDone(false);
    setImmVerifyError('');
    setImmVerifyAttempt(0);
    setImmVerifyMessage('');
    setImmVerifyResult(null);
    setImmMintBusy(false);
    setImmMintAttempted(false);
    setImmMintError('');
    setImmMintResult(null);
    setImmDisclosureAccepted(false);
  };

  useEffect(() => {
    if (immOpen && immStep === 1) {
      setTimeout(() => immFirstBtnRef.current?.focus(), 0);
    }
  }, [immOpen, immStep]);

  useEffect(() => {
    // Load memory
    let alive = true;
    (async () => {
      if (!currentUser) {
        if (alive) setStatus('signin_required');
        return;
      }
      try {
        const doc = await getMemoryById(id);
        if (!alive) return;
        if (!doc) setStatus('notfound');
        else {
          setMemory(doc);
          setStatus('ready');
        }
      } catch (e) {
        console.error('Failed to load memory:', e);
        if (!alive) return;
        if (e?.code === 'permission-denied') setStatus('protected');
        else setStatus('error');
      }
    })();
    return () => {
      alive = false;
    };
  }, [id, currentUser]);

  useEffect(() => {
    // Handle focus when the report dialog opens/closes
    const wasOpen = prevReportOpenRef.current;
    if (reportOpen && !wasOpen) {
      setTimeout(() => reportFirstControlRef.current?.focus(), 0);
    } else if (!reportOpen && wasOpen) {
      setTimeout(() => reportTriggerRef.current?.focus(), 0);
    }
    prevReportOpenRef.current = reportOpen;
  }, [reportOpen]);

  const isOwner = !!(currentUser && memory && currentUser.uid === memory.userId);
  const isPublic = !!memory?.isPublic;
  const isLinkedWalletActive = walletMatchesLinkedAddress(walletAddress, linkedWallet);
  const walletReservedToCurrentUser =
    !!currentUser?.uid && walletReservation?.ownerUid === currentUser.uid;
  const walletReservedToAnotherUser =
    !!walletReservation?.ownerUid && walletReservation.ownerUid !== currentUser?.uid;
  const hasLinkedWallet = !!linkedWallet?.primaryAddress;

  const getWalletLinkError = () => {
    if (linkedWalletLoading || walletReservationLoading) return 'Checking the wallet linked to this BWK account.';
    if (!hasLinkedWallet) return 'Link this wallet to your BWK account before minting.';
    if (!isLinkedWalletActive) return 'Connect the wallet linked to this BWK account before minting.';
    if (walletReservedToAnotherUser) return 'This wallet is already linked to another BWK account.';
    if (!walletReservedToCurrentUser) return 'Confirm this wallet link before minting.';
    return '';
  };

  // ✅ Minted state (works for all previously minted memories)
  const mintLatest = mintStatus.latest || buildMintRecord(getMemoryNftLatest(memory) || {});
  const isMinted = !!mintStatus.isMinted;
  const pendingNft = memory?.nftPending || memory?.nft?.pending || null;
  const hasPendingNft = !!(
    pendingNft &&
    !isMinted &&
    (pendingNft.tokenUri || pendingNft.metadataCid || pendingNft.gatewayUrl)
  );
  const pendingUsesPublicMintPolicy = pendingNft?.visibilityPolicy === 'public-memory-v1';
  const isPublicForMint = !!(memory?.isPublic && memory?.isMediaPublic);
  const mintLat = memory?.location?.lat ?? memory?.lat;
  const mintLng = memory?.location?.lng ?? memory?.lng;
  const hasMintLocation =
    Number.isFinite(Number(mintLat)) && Number.isFinite(Number(mintLng));
  const mintDisclosureItems = useMemo(
    () => buildMintDisclosureItems({ memory, memoryId: id, walletAddress }),
    [id, memory, walletAddress]
  );

  useEffect(() => {
    if (!memory) {
      setMintStatus({
        isMinted: false,
        latest: null,
        source: 'none',
        chainConfirmed: false,
      });
      return undefined;
    }

    const localLatest = buildMintRecord(getMemoryNftLatest(memory) || {});
    const localIsMinted = isMemoryMarkedMinted(memory);

    setMintStatus({
      isMinted: localIsMinted,
      latest: localIsMinted ? localLatest : null,
      source: localIsMinted ? 'firestore' : 'none',
      chainConfirmed: false,
    });

    let alive = true;

    (async () => {
      const resolved = await resolveMemoryMintStatus({
        memory,
        ownerAddress: walletAddress,
      });

      if (!alive) return;
      setMintStatus(resolved);
    })();

    return () => {
      alive = false;
    };
  }, [memory, walletAddress]);

  const applyLocalMintState = (latestInput, source = 'local') => {
    const latestRecord = buildMintRecord(latestInput);

    setMemory((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        nft: {
          ...(prev.nft || {}),
          latest: latestRecord,
        },
        nftPending: null,
      };
    });

    setMintStatus({
      isMinted: true,
      latest: latestRecord,
      source,
      chainConfirmed: !!latestRecord.tokenId,
    });
  };

  const persistMintToFirestore = async (latestInput = mintLatest) => {
    const latestRecord = buildMintRecord(latestInput || {});
    if (!id) {
      toast.error('Missing memory ID.');
      return;
    }
    if (!latestRecord?.tokenUri && !latestRecord?.txHash && latestRecord?.tokenId == null) {
      toast.error('No mint record is available to save.');
      return;
    }

    setMintPersistBusy(true);
    try {
      await saveMemoryNftMint({
        memoryId: id,
        ownerUid: memory?.userId || null,
        chainId: latestRecord.chainId ?? 80002,
        network: latestRecord.network || 'polygon-amoy',
        contractAddress: latestRecord.contractAddress || latestRecord.contract || NFT_COLLECTION_ADDRESS,
        tokenId: latestRecord.tokenId,
        txHash: latestRecord.txHash,
        explorerUrl: latestRecord.explorerUrl,
        tokenUri: latestRecord.tokenUri,
        metadataCid: latestRecord.metadataCid,
        imageCid: latestRecord.imageCid,
        provider: latestRecord.provider || null,
        gatewayUrl: latestRecord.gatewayUrl || null,
        toAddress: latestRecord.toAddress,
        minterAddress: latestRecord.minterAddress,
        mintedAtIso:
          typeof latestRecord.mintedAt === 'string'
            ? latestRecord.mintedAt
            : new Date().toISOString(),
        status: latestRecord.status || 'success',
        mirror: latestRecord.mirror || null,
      });
      applyLocalMintState(latestRecord, 'firestore');
      toast.success('Minted status saved to Firebase.');
    } catch (err) {
      console.error('[BWK][DB] persistMintToFirestore failed:', err);
      toast.error(
        err?.code === 'permission-denied'
          ? 'Firebase still rejected the minted status write.'
          : 'Could not save minted status to Firebase.'
      );
    } finally {
      setMintPersistBusy(false);
    }
  };

  const title =
    (memory?.title && String(memory.title).trim()) ||
    (memory?.name && String(memory.name).trim()) ||
    (memory?.story ? String(memory.story).trim().slice(0, 48) : '') ||
    '';

  const images = useMemo(() => {
    if (!memory) return [];
    if (Array.isArray(memory.imageUrls)) return memory.imageUrls;
    if (memory.imageUrl) return [memory.imageUrl];
    return [];
  }, [memory]);

  const firstImage = images[0] || null;
  const hasStory = Boolean(memory?.story && String(memory.story).trim().length);

  // ✅ Read coords from memory and update debug info (v29 logic)
  const [center, hasCoords] = useMemo(() => {
    if (!memory) return [null, false];

    const lat = memory?.location?.lat ?? memory?.lat;
    const lng = memory?.location?.lng ?? memory?.lng;

    const ok = Number.isFinite(Number(lat)) && Number.isFinite(Number(lng));

    return [ok ? { lat: Number(lat), lng: Number(lng) } : null, ok];
  }, [memory]);

  // Location label
  const locationLabel = useMemo(() => {
    if (!memory) return '';
    const region = memory.region || memory.state || memory.administrative_area;
    const city =
      memory.city ||
      memory.locality ||
      memory.town ||
      memory.municipality ||
      memory.placeName;

    const parts = [];
    if (region) parts.push(String(region).trim());
    if (city) parts.push(String(city).trim());
    if (parts.length) return parts.join(', ');

    const lat = memory?.location?.lat ?? memory?.lat;
    const lng = memory?.location?.lng ?? memory?.lng;
    if (Number.isFinite(Number(lat)) && Number.isFinite(Number(lng))) {
      return `${Number(lat).toFixed(4)}, ${Number(lng).toFixed(4)}`;
    }
    return '';
  }, [memory]);

  const pageUrl = `${typeof window !== 'undefined' ? window.location.origin : ''}/m/${id}`;

  // Owner always sees media; otherwise it must be public *and* viewer logged-in
  const canShowMedia = !!(memory && (isOwner || (memory.isMediaPublic && !!currentUser)));

  const seoTitle = title ? `${title} • Blessed with a Kiss` : 'Memory • Blessed with a Kiss';
  const descriptionBase = [
    memory?.date ? `On ${memory.date}` : null,
    locationLabel ? `at ${locationLabel}` : null,
    hasStory ? String(memory.story).trim().slice(0, 140) : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const seoDescription = descriptionBase || 'A memory captured on the map with Blessed with a Kiss.';
  const seoImage = canShowMedia && firstImage ? firstImage : '/logo192.png';

  const confirmCopied = (msg = 'Link copied!') => {
    setCopied(true);
    setLiveMsg('Link copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
    try {
      toast.success(msg);
    } catch {}
  };
  const doCopy = () => {
    const input = copyHelperRef.current || document.createElement('input');
    if (!copyHelperRef.current) {
      input.type = 'text';
      input.style.position = 'fixed';
      input.style.left = '-9999px';
      document.body.appendChild(input);
      copyHelperRef.current = input;
    }
    input.value = pageUrl;
    input.select();
    input.setSelectionRange(0, pageUrl.length);
    document.execCommand('copy');
    confirmCopied();
  };

  const handleShareClick = async () => {
    const canWebShare = typeof navigator.share === 'function';
    const secure = window.isSecureContext || window.location.hostname === 'localhost';
    if (canWebShare && secure) {
      try {
        await navigator.share({ title: title || 'Memory', text: 'Open this memory', url: pageUrl });
        try {
          await navigator.clipboard?.writeText?.(pageUrl);
        } catch {}
        confirmCopied('Shared!');
        return;
      } catch {}
    }
    setShareOpen(true);
  };

  const openShareTarget = (href, alsoCopy = false) => {
    window.open(href, '_blank', 'noopener,noreferrer');
    if (alsoCopy) doCopy();
  };

  const ensureExportImage = async () => {
    if (!firstImage) return true;
    try {
      await new Promise((resolve, reject) => {
        const im = new Image();
        im.crossOrigin = 'anonymous';
        im.referrerPolicy = 'no-referrer';
        const sep = firstImage.includes('?') ? '&' : '?';
        im.onload = () => resolve(true);
        im.onerror = reject;
        im.src = `${firstImage}${sep}cb=${Date.now()}`;
      });
      return true;
    } catch {
      return false;
    }
  };

  async function renderNodeToCanvas(node) {
    const html2canvas = await getHtml2Canvas().catch((err) => {
      console.error('Failed to load html2canvas:', err);
      toast.error('Export module failed to load.');
      return null;
    });
    if (!html2canvas) return null;

    await new Promise((r) => setTimeout(r, 20));
    const targetSize = 1080;
    const canvas = await html2canvas(node, {
      backgroundColor: IVORY,
      useCORS: true,
      allowTaint: false,
      scale: 1,
      width: targetSize,
      height: targetSize,
      windowWidth: targetSize,
      windowHeight: targetSize,
      logging: false,
    });
    return canvas;
  }

  const handleDownload = async () => {
    try {
      const preferred = exportRef.current;
      const fallback = visibleCardRef.current;
      if (!preferred && !fallback) {
        try {
          toast.error('Nothing to capture.');
        } catch {}
        return;
      }

      const imageOk = await ensureExportImage();
      let hiddenImg;
      const capture = async (node) => {
        if (!imageOk) {
          const imgEl = node.querySelector?.('[data-export-image]');
          if (imgEl) {
            hiddenImg = imgEl;
            imgEl.style.display = 'none';
          }
        }
        return renderNodeToCanvas(node);
      };

      let canvas = preferred ? await capture(preferred) : null;
      if (!canvas || canvas.width === 0 || canvas.height === 0) {
        if (fallback) canvas = await capture(fallback);
      }
      if (!canvas || canvas.width === 0 || canvas.height === 0) {
        if (hiddenImg) hiddenImg.style.display = '';
        try {
          toast.error('Export failed (empty canvas).');
        } catch {}
        return;
      }

      let blob = await new Promise((res) => canvas.toBlob(res, 'image/png', 0.95));
      let href;
      if (blob) {
        href = URL.createObjectURL(blob);
      } else {
        const dataUrl = canvas.toDataURL('image/png');
        if (!dataUrl || dataUrl === 'data:,') {
          if (hiddenImg) hiddenImg.style.display = '';
          try {
            toast.error('Export failed.');
          } catch {}
          return;
        }
        href = dataUrl;
      }

      const a = document.createElement('a');
      const safeTitle = (title || 'Blessed_with_a_kiss').replace(/[^\w\-]+/g, '_');
      a.download = `bwk-${safeTitle}-${id}.png`;
      a.href = href;
      document.body.appendChild(a);
      a.click();
      a.remove();
      if (href.startsWith('blob:')) URL.revokeObjectURL(href);

      if (hiddenImg) hiddenImg.style.display = '';
      try {
        toast.success('Image downloaded.');
      } catch {}
    } catch (err) {
      console.error('Export failed:', err);
      try {
        toast.error('Could not create image.');
      } catch {}
    }
  };

  // Brand pin as Google Maps SymbolPath circle (from v29)
  const pinIcon = useMemo(() => {
    if (!isLoaded || typeof window === 'undefined' || !window.google?.maps) {
      return null;
    }

    const gm = window.google.maps;
    const icon = {
      path: gm.SymbolPath.CIRCLE,
      scale: 8,
      fillColor: GOLD,
      fillOpacity: 1,
      strokeColor: NAVY,
      strokeWeight: 2,
    };

    return icon;
  }, [isLoaded]);

  const encodedUrl = encodeURIComponent(pageUrl);
  const encodedText = encodeURIComponent(title || 'Memory');
  const whatsapp = `https://wa.me/?text=${encodedText}%20${encodedUrl}`;
  const x_twitter = `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`;
  const facebook = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
  const email = `mailto:?subject=${encodedText}&body=${encodedUrl}`;

  // ==========================
  // Immortalize flow helpers
  // ==========================
  const loadPendingUploadIntoWizard = (pending) => {
    if (!pending) return false;
    if (pending.visibilityPolicy !== 'public-memory-v1') return false;

    const metadataCid = pending.metadataCid || '';
    const gatewayBase = String(pending.gatewayBase || 'https://gateway.pinata.cloud/ipfs/').trim();
    const safeGatewayBase = gatewayBase.replace(/\/+$/, '') + '/';
    const gatewayUrl = pending.gatewayUrl || (metadataCid ? `${safeGatewayBase}${metadataCid}` : '');

    setImmUploadResult({
      tokenUri: pending.tokenUri || (metadataCid ? `ipfs://${metadataCid}` : ''),
      imageCid: pending.imageCid || '',
      metadataCid,
      metadata: null,
      provider: pending.provider || 'pinata',
      gatewayBase: safeGatewayBase,
      gatewayUrl,
      mirror: pending.mirror || null,
    });

    setImmUploadDone(true);
    setImmUploadError('');

    const verification = pending.verification || null;
    if (verification?.ok) {
      setImmVerifyDone(true);
      setImmVerifyResult(verification);
      setImmVerifyError('');
      setImmVerifyMessage('Public links were already verified.');
    } else {
      setImmVerifyDone(false);
      setImmVerifyResult(null);
      setImmVerifyError('');
      setImmVerifyMessage('Ready to verify the existing upload.');
    }

    return true;
  };

  const continueImmortalizeAfterPublicCheck = (forceFreshUpload = false) => {
    if (!forceFreshUpload && hasPendingNft && pendingUsesPublicMintPolicy && loadPendingUploadIntoWizard(pendingNft)) {
      setImmStep(3);
      return;
    }

    setImmUploadBusy(false);
    setImmUploadDone(false);
    setImmUploadError('');
    setImmUploadResult(null);
    setImmVerifyBusy(false);
    setImmVerifyDone(false);
    setImmVerifyError('');
    setImmVerifyAttempt(0);
    setImmVerifyMessage('');
    setImmVerifyResult(null);
    setImmStep(2);
  };

  const handleImmortalizeConfirm = async () => {
    if (!memory) {
      setImmPublishError('Memory is not loaded yet.');
      return;
    }
    if (!hasMintLocation) {
      setImmPublishError('Add a location before immortalizing this memory.');
      return;
    }

    if (isPublicForMint) {
      continueImmortalizeAfterPublicCheck(false);
      return;
    }

    setImmPublishBusy(true);
    setImmPublishError('');

    try {
      await updateMemoryDoc(id, {
        isPublic: true,
        isMediaPublic: true,
        updatedAt: serverTimestamp(),
      });

      setMemory((prev) => prev ? ({
        ...prev,
        isPublic: true,
        isMediaPublic: true,
        updatedAt: new Date(),
        nftPending: null,
      }) : prev);

      toast.success('Memory is public now. Creating fresh public NFT proof.');
      continueImmortalizeAfterPublicCheck(true);
    } catch (err) {
      const message = err?.message || 'Could not make this memory public.';
      setImmPublishError(message);
      toast.error(message);
    } finally {
      setImmPublishBusy(false);
    }
  };

  const openImmortalizeWizard = async () => {
    if (isMinted) {
      toast.success('This memory is already minted.');
      return;
    }

    if (!currentUser) {
      toast.error('Please log in to mint this memory.');
      return;
    }
    if (!isOwner) {
      toast.error('Only the owner can mint this memory.');
      return;
    }
    if (!walletAddress) {
      toast.error('Please connect your wallet to mint this memory.');
      return;
    }
    const walletLinkError = getWalletLinkError();
    if (walletLinkError) {
      toast.error(walletLinkError);
      return;
    }
    if (!exportRef.current) {
      toast.error('Export card is not ready yet.');
      return;
    }

    closeImmModal();
    setImmOpen(true);
    if (isPublicForMint && hasPendingNft && pendingUsesPublicMintPolicy && loadPendingUploadIntoWizard(pendingNft)) {
      setImmStep(3);
    } else {
      setImmStep(1);
    }
  };

  const buildPngBlobForMint = async () => {
    await ensureExportImage();

    const node = exportRef.current;
    if (!node) throw new Error('Export card is not ready yet.');

    const canvas = await renderNodeToCanvas(node);
    if (!canvas) throw new Error('Could not render card for minting.');
    if (canvas.width !== 1080 || canvas.height !== 1080) {
      throw new Error(`NFT image must render as 1080x1080. Got ${canvas.width}x${canvas.height}.`);
    }

    const imageBlob = await new Promise((resolve, reject) => {
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Empty PNG blob'))), 'image/png', 0.95);
    });

    return imageBlob;
  };

  const runUploadStep = async () => {
    if (!memory) throw new Error('Memory not loaded yet.');
    if (!walletAddress) throw new Error('Wallet not connected.');
    if (!hasMintLocation) throw new Error('Add a location before immortalizing this memory.');
    if (!isPublicForMint) {
      throw new Error('Make this memory fully public before immortalizing it.');
    }
    const walletLinkError = getWalletLinkError();
    if (walletLinkError) throw new Error(walletLinkError);
    if (!exportRef.current) throw new Error('Export card is not ready yet.');

    setImmUploadBusy(true);
    setImmUploadError('');
    setImmUploadDone(false);

    try {
      const imageBlob = await buildPngBlobForMint();

      const { tokenUri, imageCid, metadataCid, metadata, provider, gatewayBase, mirror } = await uploadMemoryNftToIpfs({
        imageBlob,
        memory,
        memoryId: id,
        ownerUid: memory.userId,
        network: 'polygon-amoy',
        minterAddress: walletAddress,
        tokenId: null,
        externalBaseUrl: window.location.origin,
        mintedAtOverride: undefined,
      });

      const safeGatewayBase = String(gatewayBase || '').trim().replace(/\/+$/, '') + '/';
      const gatewayUrl = `${safeGatewayBase}${metadataCid}`;

      setImmUploadResult({
        tokenUri,
        imageCid,
        metadataCid,
        metadata,
        gatewayUrl,
        provider: provider || 'pinata',
        gatewayBase: safeGatewayBase,
        mirror: mirror || null,
      });

      await saveMemoryNftPending({
        memoryId: id,
        ownerUid: memory.userId,
        chainId: 80002,
        network: 'polygon-amoy',
        tokenUri,
        imageCid,
        metadataCid,
        provider: provider || 'pinata',
        gatewayBase: safeGatewayBase,
        gatewayUrl,
        mirror: mirror || null,
        visibilityPolicy: 'public-memory-v1',
        status: 'uploaded_unverified',
        verification: {
          status: 'not_started',
          ok: false,
          message: 'Upload saved. Public access has not been verified yet.',
          metadataUrl: gatewayUrl,
        },
      });

      setMemory((prev) => prev ? ({
        ...prev,
        nftPending: {
          status: 'uploaded_unverified',
          chainId: 80002,
          network: 'polygon-amoy',
          tokenUri,
          imageCid,
          metadataCid,
          provider: provider || 'pinata',
          gatewayBase: safeGatewayBase,
          gatewayUrl,
          ownerUid: memory.userId,
          mirror: mirror || null,
          visibilityPolicy: 'public-memory-v1',
          verification: {
            status: 'not_started',
            ok: false,
            message: 'Upload saved. Public access has not been verified yet.',
            metadataUrl: gatewayUrl,
          },
        },
      }) : prev);

      setImmVerifyBusy(false);
      setImmVerifyDone(false);
      setImmVerifyError('');
      setImmVerifyAttempt(0);
      setImmVerifyMessage('Upload saved. Ready to verify public access.');
      setImmVerifyResult(null);

      setImmUploadDone(true);
      try {
        toast.success(`Uploaded to ${(provider || 'Pinata').replace(/^./, (c) => c.toUpperCase())}.`);
      } catch {}
    } catch (err) {
      console.error('[BWK][Immortalize] Upload step failed:', err);
      setImmUploadError(err?.message || 'Upload failed.');
      try {
        toast.error(err?.message || 'Upload failed.');
      } catch {}
    } finally {
      setImmUploadBusy(false);
    }
  };

  const runVerifyStep = async () => {
    if (!immUploadResult?.gatewayUrl) {
      setImmVerifyError('Missing metadata gateway URL. Please upload again.');
      return;
    }

    setImmVerifyBusy(true);
    setImmVerifyDone(false);
    setImmVerifyError('');
    setImmVerifyAttempt(0);
    setImmVerifyMessage('Checking public metadata access...');

    try {
      const result = await verifyUploadedNftAssets({
        metadataUrl: immUploadResult.gatewayUrl,
        gatewayBase: immUploadResult.gatewayBase,
        attempts: 5,
        delayMs: 3000,
        onProgress: (event) => {
          if (event?.attempt) setImmVerifyAttempt(event.attempt);
          if (event?.stage === 'metadata') {
            setImmVerifyMessage(`Attempt ${event.attempt}: checking metadata JSON...`);
          } else if (event?.stage === 'image') {
            setImmVerifyMessage(`Attempt ${event.attempt}: checking NFT image...`);
          } else if (event?.stage === 'retry') {
            setImmVerifyMessage(`Attempt ${event.attempt} failed. Retrying public gateway access...`);
          } else if (event?.stage === 'done') {
            setImmVerifyMessage('Metadata and image are publicly retrievable.');
          }
        },
      });

      const verification = {
        status: 'verified',
        ok: true,
        attemptsUsed: result.attemptsUsed,
        message: 'Metadata and image are publicly retrievable.',
        metadataUrl: result.metadataUrl,
        imageUrl: result.imageUrl,
        verifiedAt: result.verifiedAt,
      };

      await saveMemoryNftPending({
        memoryId: id,
        ownerUid: memory?.userId || null,
        chainId: 80002,
        network: 'polygon-amoy',
        tokenUri: immUploadResult.tokenUri,
        imageCid: immUploadResult.imageCid,
        metadataCid: immUploadResult.metadataCid,
        provider: immUploadResult.provider || 'pinata',
        gatewayBase: immUploadResult.gatewayBase || null,
        gatewayUrl: immUploadResult.gatewayUrl || null,
        mirror: immUploadResult.mirror || null,
        visibilityPolicy: 'public-memory-v1',
        status: 'verified_ready_to_mint',
        verification,
      });

      setImmVerifyResult(verification);
      setImmVerifyDone(true);
      setImmVerifyMessage(verification.message);
      setMemory((prev) => prev ? ({
        ...prev,
        nftPending: {
          ...(prev.nftPending || prev.nft?.pending || {}),
          visibilityPolicy: 'public-memory-v1',
          status: 'verified_ready_to_mint',
          verification,
        },
      }) : prev);
      try {
        toast.success('Public NFT files verified.');
      } catch {}
    } catch (err) {
      const message = err?.message || 'Public metadata/image verification failed.';
      const verification = {
        status: 'failed',
        ok: false,
        attemptsUsed: immVerifyAttempt || 5,
        message,
        metadataUrl: immUploadResult.gatewayUrl,
        error: message,
        failedAt: new Date().toISOString(),
      };

      setImmVerifyError(message);
      setImmVerifyResult(verification);
      setImmVerifyMessage('Upload is saved. You can retry verification later.');

      try {
        await saveMemoryNftPending({
          memoryId: id,
          ownerUid: memory?.userId || null,
          chainId: 80002,
          network: 'polygon-amoy',
          tokenUri: immUploadResult.tokenUri,
          imageCid: immUploadResult.imageCid,
          metadataCid: immUploadResult.metadataCid,
          provider: immUploadResult.provider || 'pinata',
          gatewayBase: immUploadResult.gatewayBase || null,
          gatewayUrl: immUploadResult.gatewayUrl || null,
          mirror: immUploadResult.mirror || null,
          visibilityPolicy: 'public-memory-v1',
          status: 'verification_failed',
          verification,
        });
      } catch (saveErr) {
        console.warn('[BWK][DB] Could not save verification failure state:', saveErr);
      }

      try {
        toast.error('Upload saved, but public access is not ready yet.');
      } catch {}
    } finally {
      setImmVerifyBusy(false);
    }
  };

  const runMintStep = async () => {
    if (!immDisclosureAccepted) {
      setImmMintError('Confirm that you understand what will become public before opening your wallet.');
      return;
    }
    if (!walletAddress) {
      setImmMintError('Please connect your wallet.');
      return;
    }
    const walletLinkError = getWalletLinkError();
    if (walletLinkError) {
      setImmMintError(walletLinkError);
      return;
    }

    // ✅ NEW guard: Thirdweb account object must exist for v5 sendTransaction
    if (!account) {
      setImmMintError('Thirdweb account not ready. Please reconnect your wallet.');
      return;
    }

    if (!immUploadResult?.tokenUri) {
      setImmMintError('Missing tokenUri. Please re-upload.');
      return;
    }

    if (!immVerifyDone && !immVerifyResult?.ok) {
      setImmMintError('Public metadata and image must be verified before minting.');
      return;
    }

    setImmMintBusy(true);
    setImmMintAttempted(true);
    setImmMintError('');

    try {
      const { txHash, tokenId } = await mintMemoryOnChain({
        account, // ✅ FIX: pass signer account
        to: walletAddress, // explicit receiver (same wallet)
        tokenUri: immUploadResult.tokenUri,
      });

      const explorerUrl = `https://amoy.polygonscan.com/tx/${txHash}`;
      setImmMintResult({ txHash, tokenId, explorerUrl });
      const latestMintRecord = buildMintRecord({
        chainId: 80002,
        network: 'polygon-amoy',
        contractAddress: NFT_COLLECTION_ADDRESS,
        tokenId,
        txHash,
        explorerUrl,
        tokenUri: immUploadResult.tokenUri,
        metadataCid: immUploadResult.metadataCid,
        imageCid: immUploadResult.imageCid,
        provider: immUploadResult.provider || 'pinata',
        gatewayUrl: immUploadResult.gatewayUrl || null,
        mirror: immUploadResult.mirror || null,
        toAddress: walletAddress,
        minterAddress: walletAddress,
        mintedAt: new Date().toISOString(),
      });

      applyLocalMintState(latestMintRecord, 'local');

      // ✅ NEW: persist mint results to Firestore (non-blocking)
      try {
        await saveMemoryNftMint({
          memoryId: id,
          ownerUid: memory?.userId || null,
          chainId: 80002,
          network: 'polygon-amoy',
          contractAddress: NFT_COLLECTION_ADDRESS,
          tokenId,
          txHash,
          explorerUrl,
          tokenUri: immUploadResult.tokenUri,
          metadataCid: immUploadResult.metadataCid,
          imageCid: immUploadResult.imageCid,
          provider: immUploadResult.provider || 'pinata',
          gatewayUrl: immUploadResult.gatewayUrl || null,
          toAddress: walletAddress,
          minterAddress: walletAddress,
          mintedAtIso: new Date().toISOString(),
          mirror: immUploadResult.mirror || null,
        });
        applyLocalMintState(latestMintRecord, 'firestore');
      } catch (e) {
        console.warn('[BWK][DB] saveMemoryNftMint failed after successful mint:', e?.message || e);
        try {
          toast.error('NFT minted on-chain, but BWK could not save the minted status to Firebase.');
        } catch {}
      }

      try {
        toast.success('NFT minted successfully! 🎉');
      } catch {}

      closeImmModal();
    } catch (err) {
      console.error('[BWK][Immortalize] Mint step failed:', err);
      const msg = err?.message || 'Minting failed.';
      setImmMintError(msg);

      try {
        toast.error(msg);
      } catch {}
    } finally {
      setImmMintBusy(false);
    }
  };

  // ✅ FIX: hooks MUST be above early returns (rules-of-hooks)
  // Auto-run Step 2 upload when wizard enters step 2
  useEffect(() => {
    if (!immOpen) return;
    if (immStep !== 2) return;
    if (immUploadBusy || immUploadDone) return;

    // guard: make sure memory is actually loaded before auto-running upload
    if (!memory) return;

    setImmMintBusy(false);
    setImmMintAttempted(false);
    setImmMintError('');
    setImmMintResult(null);

    runUploadStep();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [immOpen, immStep, memory]);

  // Auto-run Step 3 verification when wizard enters the public access check.
  useEffect(() => {
    if (!immOpen) return;
    if (immStep !== 3) return;
    if (!immUploadDone || !immUploadResult?.gatewayUrl) return;
    if (immVerifyBusy || immVerifyDone) return;

    setImmMintBusy(false);
    setImmMintAttempted(false);
    setImmMintError('');
    setImmMintResult(null);

    runVerifyStep();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [immOpen, immStep, immUploadDone, immUploadResult?.gatewayUrl]);

  // Wizard key handling (Escape)
  useEffect(() => {
    if (!immOpen) return;

    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;

      if (immStep === 1) {
        closeImmModal();
        return;
      }
      if ((immStep === 3 && !!immVerifyError) || (immStep === 4 && immMintAttempted && !!immMintError)) {
        closeImmModal();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [immOpen, immStep, immVerifyError, immMintAttempted, immMintError]);

  const wizardCanBackdropClose =
    immStep === 1 || (immStep === 3 && !!immVerifyError) || (immStep === 4 && immMintAttempted && !!immMintError);

  // NEW: progress checklist helpers
  const progressItem = (stepNum, label) => {
    const done =
      immStep > stepNum ||
      (stepNum === 2 && immUploadDone) ||
      (stepNum === 3 && immVerifyDone) ||
      (stepNum === 4 && immMintResult?.txHash);
    const active = immStep === stepNum;

    const icon = done ? '✅' : active ? '⏳' : '⬜';
    return (
      <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 14 }}>{icon}</span>
        <span
          style={{
            fontSize: 12,
            fontWeight: active ? 900 : 700,
            color: active ? NAVY : WARM_GRAY,
          }}
        >
          {label}
        </span>
      </div>
    );
  };

  // Report submit
  const submitReport = async (e) => {
    e?.preventDefault?.();
    if (!currentUser?.uid) {
      toast.error('Please log in to report.');
      return;
    }
    if (isOwner) {
      toast.error('You cannot report your own memory.');
      return;
    }
    setReportBusy(true);
    try {
      const res = await createFlag({
        memoryId: id,
        reporterUid: currentUser.uid,
        ownerUid: memory.userId,
        reason,
        note: note.trim() || null,
        memoryTitle: title || (memory.date || 'Untitled'),
      });
      if (res?.dedupe) toast.success('You already reported this. Our team will review it.');
      else toast.success('Thanks — your report has been submitted.');
      setReportOpen(false);
      setNote('');
      setReason('spam');
    } catch (err) {
      console.error('[Report] createFlag failed:', err);
      toast.error('Could not send report. Please try novamente.');
    } finally {
      setReportBusy(false);
    }
  };

  // ==========================
  // Early returns (NOW SAFE)
  // ==========================
  if (status === 'loading') return <div style={{ padding: 20, color: IVORY }}>Loading memory…</div>;
  if (status === 'error') return <div style={{ padding: 20, color: GOLD }}>Failed to load memory.</div>;

  if (status === 'signin_required') {
    return (
      <div style={{ padding: 20, color: IVORY }}>
        <Seo
          title="Sign in to view this memory • Blessed with a Kiss"
          description="This memory is available inside the signed-in BWK experience."
          noindex
        />
        <h1 style={{ marginTop: 0, color: IVORY }}>Sign in to view this memory</h1>
        <p style={{ color: 'rgba(253,252,249,0.82)', maxWidth: 560 }}>
          BWK keeps real memory content inside the signed-in experience. Sign in to
          view public memories, or create an account to start your own map.
        </p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 16 }}>
          <Link to="/login" style={styles.ghostBtn}>
            Sign In
          </Link>
          <Link to="/register" style={styles.primaryBtn}>
            Create Account
          </Link>
          <Link to="/map" style={styles.ghostBtn}>
            View Map Preview
          </Link>
        </div>
      </div>
    );
  }

  if (status === 'protected' || status === 'notfound' || (!isPublic && !isOwner)) {
    return (
      <div style={{ padding: 20, color: IVORY }}>
        <Seo title="Memory not found • Blessed with a Kiss" description="This memory is unavailable." noindex />
        Memory not found.
      </div>
    );
  }
  if (!memory) return null;

  return (
    <div style={styles.page}>
      <Seo title={seoTitle} description={seoDescription} url={pageUrl} image={seoImage} type="article" noindex={false} />

      <div aria-live="polite" style={{ position: 'absolute', left: -9999, top: 0 }}>
        {liveMsg}
      </div>
      <input ref={copyHelperRef} type="text" style={{ position: 'fixed', left: -9999, top: 0 }} aria-hidden />

      <div style={styles.navRow}>
        <Link to="/" style={styles.back}>
          &larr; Back
        </Link>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <Link to="/map" style={styles.ghostBtn}>
            Global Map
          </Link>
          {!isOwner && currentUser ? (
            <button
              type="button"
              style={styles.reportBtn}
              onClick={() => setReportOpen(true)}
              title="Report this memory"
              ref={reportTriggerRef}
            >
              Report
            </button>
          ) : null}
          <button
            ref={shareBtnRef}
            type="button"
            onClick={handleShareClick}
            style={{ ...styles.copyBtn, ...(copied ? styles.copyBtnSuccess : null) }}
            aria-label="Share or copy memory link"
            data-testid="share-button"
          >
            {copied ? 'Copied!' : 'Share'}
          </button>
          <button type="button" onClick={doCopy} style={styles.copyBtn}>
            Copy Link
          </button>
          <button type="button" onClick={handleDownload} style={styles.copyBtn}>
            Download as image
          </button>
        </div>
      </div>

      {/* Special silver CTA row */}
      {isOwner && (
        <div style={styles.specialRow}>
          {!isMinted && getWalletLinkError() ? (
            <div style={styles.walletLinkNotice}>
              {getWalletLinkError()} Use the wallet panel in your account menu to link or switch wallets.
            </div>
          ) : null}
          <button
            type="button"
            style={{
              ...styles.silverButton,
              opacity: isMinted ? 0.6 : 1,
              cursor: isMinted ? 'not-allowed' : 'pointer',
            }}
            onClick={isMinted ? undefined : openImmortalizeWizard}
            disabled={isMinted}
            aria-disabled={isMinted}
            title={isMinted ? 'This memory is already minted' : hasPendingNft ? 'Resume the saved NFT upload' : 'Mint this memory as an NFT'}
          >
            {isMinted ? 'MEMORY ALREADY MINTED' : hasPendingNft ? 'RESUME IMMORTALIZING' : 'IMMORTALIZE THIS MEMORY'}
          </button>
        </div>
      )}

      {/* ✅ Minted status box (visible to anyone who can view the memory) */}
      {isMinted && mintLatest && (
        <div style={styles.mintedBox}>
          <div style={{ fontWeight: 900, marginBottom: 4 }}>🧾 This memory is immortalized</div>
          <div style={{ fontSize: 13, color: WARM_GRAY, marginBottom: 6 }}>
            {mintLatest?.tokenId != null ? (
              <>Token #{String(mintLatest.tokenId)}</>
            ) : (
              <>Mint confirmed</>
            )}
            {mintLatest?.network ? <> · {mintLatest.network}</> : null}
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
            {mintLatest?.txHash ? (
              <span style={styles.mintedChip} title={mintLatest.txHash}>
                Tx: {shortAddr(String(mintLatest.txHash))}
              </span>
            ) : null}

            {mintLatest?.toAddress ? (
              <span style={styles.mintedChip} title={mintLatest.toAddress}>
                To: {shortAddr(String(mintLatest.toAddress))}
              </span>
            ) : null}

            {mintLatest?.explorerUrl ? (
              <a
                href={String(mintLatest.explorerUrl)}
                target="_blank"
                rel="noopener noreferrer"
                style={styles.mintedLink}
              >
                View on explorer ↗
              </a>
            ) : null}
          </div>

          {isOwner && mintStatus.source !== 'firestore' ? (
            <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: WARM_GRAY }}>
                BWK still needs to save this mint to Firebase.
              </span>
              <button
                type="button"
                style={{
                  ...styles.mintedRecoverBtn,
                  opacity: mintPersistBusy ? 0.7 : 1,
                  cursor: mintPersistBusy ? 'wait' : 'pointer',
                }}
                onClick={() => persistMintToFirestore(mintLatest)}
                disabled={mintPersistBusy}
              >
                {mintPersistBusy ? 'Saving…' : 'Save minted status'}
              </button>
            </div>
          ) : null}
        </div>
      )}

      <h1 style={styles.title}>{title || 'Blessed with a kiss 🙂'}</h1>

      <div ref={visibleCardRef} style={styles.card}>
        {memory.date && (
          <>
            <div style={styles.metaRowRight}>
              <span role="img" aria-label="date" style={{ marginRight: 8 }}>
                📅
              </span>
              <span>{memory.date}</span>
            </div>
            {locationLabel && (
              <div style={styles.metaRowRightSub}>
                <span role="img" aria-label="location" style={{ marginRight: 6 }}>
                  📍
                </span>
                <span>{locationLabel}</span>
              </div>
            )}
          </>
        )}

        {canShowMedia && firstImage && (
          <div style={{ margin: '8px 0 12px' }}>
            <img
              src={firstImage}
              referrerPolicy="no-referrer"
              alt=""
              style={styles.heroImage}
              loading="lazy"
            />
          </div>
        )}

        {hasStory && (
          <section style={styles.storyCard}>
            <div style={styles.storyLabel}>Story</div>
            <p style={styles.storyText}>{memory.story}</p>
          </section>
        )}
      </div>

      {/* Map */}
      <div style={{ marginTop: 12 }}>
        {isLoaded && hasCoords ? (
          <GoogleMap
            mapContainerStyle={mapStyle}
            center={center}
            zoom={14}
            options={withBrandMapOptions()}
          >
            {pinIcon ? (
              <Marker position={center} icon={pinIcon} />
            ) : (
              <Marker position={center} />
            )}
          </GoogleMap>
        ) : (
          <div style={{ color: 'orange', marginTop: 20 }}>Map not ready yet…</div>
        )}
      </div>

      <div id="bwk-export-card" ref={exportRef} style={styles.exportHidden}>
        <div style={styles.exportFrame}>
          <div style={styles.exportHeader}>
            <div style={styles.exportBrand}>Blessed with a Kiss</div>
            <div style={styles.exportSlogan}>the future form of diary</div>
          </div>
          <div style={styles.exportSquareBody}>
            <div style={styles.exportSquareTitle}>{title || 'Blessed with a Kiss'}</div>

            <div style={styles.exportSquareHeroRow}>
              <div style={styles.exportSquareImageCol}>
                {canShowMedia && firstImage ? (
                  <img
                    data-export-image
                    crossOrigin="anonymous"
                    referrerPolicy="no-referrer"
                    src={firstImage}
                    alt=""
                    style={styles.exportSquareImage}
                  />
                ) : (
                  <div style={styles.exportSquareImagePlaceholder}>No image attached</div>
                )}
              </div>

              <div style={styles.exportSquareMetaCol}>
                {memory.time ? <div style={styles.exportSquareTime}>🕒 {memory.time}</div> : null}
                {memory.date ? <div style={styles.exportSquareDate}>📅 {memory.date}</div> : null}
                {locationLabel ? <div style={styles.exportSquareLocation}>📍 {locationLabel}</div> : null}
              </div>
            </div>

            <div style={styles.exportSquareSpacer} />

            <div style={styles.exportSquareStory}>
              <div style={styles.exportSquareStoryLabel}>Story</div>
              <div style={styles.exportSquareStoryText}>
                {hasStory ? memory.story : 'A moment worth keeping forever.'}
              </div>
            </div>

            <div style={styles.exportSquareFooter}>bwk.app • Your story. Your proof. Forever.</div>
          </div>
          <div style={styles.exportTitle}>{title || 'Blessed with a kiss 🙂'}</div>
          {memory.date && <div style={styles.exportDate}>📅 {memory.date}</div>}
          {locationLabel && <div style={styles.exportLocation}>📍 {locationLabel}</div>}
          {canShowMedia && firstImage && (
            <img
              data-export-image
              crossOrigin="anonymous"
              referrerPolicy="no-referrer"
              src={firstImage}
              alt=""
              style={styles.exportImage}
            />
          )}
          {hasStory && (
            <div style={styles.exportStory}>
              <div style={styles.exportStoryLabel}>Story</div>
              <div style={styles.exportStoryText}>{memory.story}</div>
            </div>
          )}
          <div style={styles.exportFooter}>bwk.app • Your story. Your proof. Forever.</div>
        </div>
      </div>

      {/* Share sheet */}
      {shareOpen && (
        <div style={styles.sheetBackdrop} onClick={() => setShareOpen(false)}>
          <div style={styles.sheet} onClick={(e) => e.stopPropagation()}>
            <div style={styles.sheetTitle}>Share memory</div>
            <div style={styles.sheetRow}>
              <button style={styles.sheetBtn} onClick={() => openShareTarget(whatsapp, true)}>
                WhatsApp
              </button>
              <button style={styles.sheetBtn} onClick={() => openShareTarget(x_twitter, true)}>
                X / Twitter
              </button>
              <button style={styles.sheetBtn} onClick={() => openShareTarget(facebook, true)}>
                Facebook
              </button>
              <button style={styles.sheetBtn} onClick={() => openShareTarget(email, false)} aria-label="Share by email">
                Email
              </button>
              <button
                style={{ ...styles.sheetBtn, ...styles.sheetCopy }}
                onClick={() => doCopy()}
                aria-label="Copy link to clipboard"
              >
                Copy Link
              </button>
            </div>
            <button style={styles.sheetClose} onClick={() => setShareOpen(false)} aria-label="Close share panel">
              Close
            </button>
          </div>
        </div>
      )}

      {/* Immortalize Wizard */}
      {immOpen && (
        <div
          style={styles.modalBackdrop}
          role="dialog"
          aria-modal="true"
          aria-labelledby="immortalize-title"
          onClick={() => {
            if (!wizardCanBackdropClose) return;
            closeImmModal();
          }}
        >
          <div style={styles.immCard} onClick={(e) => e.stopPropagation()}>
            <div style={styles.immTopRow}>
              <div style={{ display: 'grid', gap: 8 }}>
                <div style={styles.immPill}>Step {immStep} of 4</div>

                {/* Progress checklist */}
                <div style={styles.immChecklist}>
                  {progressItem(1, 'Confirm')}
                  {progressItem(2, 'Upload to IPFS')}
                  {progressItem(3, 'Verify access')}
                  {progressItem(4, 'Mint on-chain')}
                </div>
              </div>

              {(immStep === 1 || (immStep === 3 && !!immVerifyError) || (immStep === 4 && immMintAttempted && !!immMintError)) && (
                <button type="button" onClick={closeImmModal} style={styles.immCloseBtn} aria-label="Close">
                  ✕
                </button>
              )}
            </div>

            {immStep === 1 && (
              <>
                <h2 id="immortalize-title" style={styles.immTitle}>
                  Immortalize this memory
                </h2>
                <p style={styles.immText}>
                  This will make a permanent public proof of this memory: the memory, media, story, location, NFT image,
                  and NFT metadata will be public before minting on <strong>Polygon Amoy</strong>.
                </p>

                <div style={styles.immInfoBox}>
                  <div style={{ fontWeight: 900, marginBottom: 6 }}>Before you continue</div>
                  <ul style={{ margin: 0, paddingLeft: 18, color: WARM_GRAY, lineHeight: 1.5 }}>
                    <li>Minting requires a public memory, public media, and a saved location.</li>
                    <li>Uploading to IPFS happens first and cannot be treated as private.</li>
                    <li>You may pay a small network gas fee when minting.</li>
                    <li>Only you (the owner) can mint this memory.</li>
                  </ul>
                </div>

                {!hasMintLocation ? (
                  <div style={styles.immErrorBox}>
                    <div style={{ fontWeight: 900 }}>Location required</div>
                    <div style={{ fontSize: 13 }}>Add a location before immortalizing this memory.</div>
                  </div>
                ) : !isPublicForMint ? (
                  <div style={styles.immInfoBox}>
                    <div style={{ fontWeight: 900, marginBottom: 6 }}>This memory is currently private</div>
                    <div style={{ color: WARM_GRAY, fontSize: 13, lineHeight: 1.5 }}>
                      Continuing will make the memory and its media public, then create fresh public NFT metadata.
                    </div>
                  </div>
                ) : null}

                {immPublishError ? (
                  <div style={styles.immErrorBox}>
                    <div style={{ fontWeight: 900 }}>Could not continue</div>
                    <div style={{ fontSize: 13 }}>{immPublishError}</div>
                  </div>
                ) : null}

                <div style={styles.immActions}>
                  <button
                    ref={immFirstBtnRef}
                    type="button"
                    style={{
                      ...styles.immBtnPrimary,
                      opacity: immPublishBusy || !hasMintLocation ? 0.65 : 1,
                      cursor: immPublishBusy || !hasMintLocation ? 'not-allowed' : 'pointer',
                    }}
                    onClick={handleImmortalizeConfirm}
                    disabled={immPublishBusy || !hasMintLocation}
                  >
                    {immPublishBusy
                      ? 'Updating...'
                      : !hasMintLocation
                        ? 'Add location first'
                        : isPublicForMint
                          ? 'Yes, continue'
                          : 'Make public and continue'}
                  </button>
                  <button type="button" style={styles.immBtnGhost} onClick={closeImmModal}>
                    No
                  </button>
                </div>
              </>
            )}

            {immStep === 2 && (
              <>
                <h2 id="immortalize-title" style={styles.immTitle}>
                  Uploading to Pinata (IPFS)
                </h2>
                <p style={styles.immText}>
                  We’re generating a PNG of your memory card and uploading both the image and metadata to IPFS via Pinata.
                  <br />
                  <span style={{ color: WARM_GRAY, fontSize: 13 }}>You can’t cancel this step once it starts.</span>
                </p>

                <div style={styles.immProgressBox}>
                  <div style={{ fontWeight: 900, marginBottom: 6 }}>
                    Status:{' '}
                    {immUploadBusy ? (
                      <span style={{ color: NAVY }}>Uploading…</span>
                    ) : immUploadDone ? (
                      <span style={{ color: TEAL }}>Upload complete</span>
                    ) : immUploadError ? (
                      <span style={{ color: BURGUNDY }}>Upload failed</span>
                    ) : (
                      <span style={{ color: WARM_GRAY }}>Starting…</span>
                    )}
                  </div>

                  {immUploadError ? (
                    <div style={styles.immErrorBox}>
                      <div style={{ fontWeight: 900 }}>Error</div>
                      <div style={{ fontSize: 13 }}>{immUploadError}</div>
                    </div>
                  ) : null}

                  {immUploadDone && immUploadResult ? (
                    <div style={{ display: 'grid', gap: 6, marginTop: 10, fontSize: 13 }}>
                      <div>
                        <strong>Primary:</strong>{' '}
                        <code style={styles.immCode}>{String(immUploadResult.provider || 'pinata')}</code>
                      </div>
                      <div>
                        <strong>Token URI:</strong> <code style={styles.immCode}>{immUploadResult.tokenUri}</code>
                      </div>
                      <div>
                        <strong>Gateway:</strong> <code style={styles.immCode}>{immUploadResult.gatewayUrl}</code>
                      </div>
                      <div>
                        <strong>Mirror:</strong>{' '}
                        <code style={styles.immCode}>
                          {immUploadResult.mirror?.metadataCid
                            ? `lighthouse (${immUploadResult.mirror.metadataCid})`
                            : 'not available'}
                        </code>
                      </div>
                    </div>
                  ) : null}
                </div>

                <div style={styles.immActions}>
                  <button
                    type="button"
                    style={{
                      ...styles.immBtnPrimary,
                      opacity: immUploadDone ? 1 : 0.6,
                      cursor: immUploadDone ? 'pointer' : 'not-allowed',
                    }}
                    disabled={!immUploadDone}
                    onClick={() => setImmStep(3)}
                  >
                    Next
                  </button>
                </div>
              </>
            )}

            {immStep === 3 && (
              <>
                <h2 id="immortalize-title" style={styles.immTitle}>
                  Verifying public NFT files
                </h2>
                <p style={styles.immText}>
                  We are checking that the metadata JSON and image are publicly retrievable before the irreversible mint.
                  <br />
                  <span style={{ color: WARM_GRAY, fontSize: 13 }}>This usually takes a few seconds, but we retry if the gateway is still warming up.</span>
                </p>

                <div style={styles.immProgressBox}>
                  <div style={{ fontWeight: 900, marginBottom: 6 }}>
                    Status:{' '}
                    {immVerifyBusy ? (
                      <span style={{ color: NAVY }}>Verifying public access...</span>
                    ) : immVerifyDone ? (
                      <span style={{ color: TEAL }}>Verified</span>
                    ) : immVerifyError ? (
                      <span style={{ color: BURGUNDY }}>Verification failed</span>
                    ) : (
                      <span style={{ color: WARM_GRAY }}>Ready to verify</span>
                    )}
                  </div>

                  <div style={{ color: WARM_GRAY, fontSize: 13, marginBottom: 8 }}>
                    {immVerifyAttempt ? `Attempt ${immVerifyAttempt} of 5. ` : null}
                    {immVerifyMessage || 'Preparing verification...'}
                  </div>

                  {immUploadResult?.gatewayUrl ? (
                    <div style={{ fontSize: 13, marginTop: 6 }}>
                      <strong>Metadata:</strong> <code style={styles.immCode}>{immUploadResult.gatewayUrl}</code>
                    </div>
                  ) : null}

                  {immVerifyResult?.imageUrl ? (
                    <div style={{ fontSize: 13, marginTop: 6 }}>
                      <strong>Image:</strong> <code style={styles.immCode}>{immVerifyResult.imageUrl}</code>
                    </div>
                  ) : null}

                  {immVerifyError ? (
                    <div style={styles.immErrorBox}>
                      <div style={{ fontWeight: 900 }}>Saved for later</div>
                      <div style={{ fontSize: 13 }}>
                        {immVerifyError}
                        <br />
                        The upload references are saved, so you can close this and retry verification later.
                      </div>
                    </div>
                  ) : null}
                </div>

                <div style={styles.immActions}>
                  {immVerifyError ? (
                    <>
                      <button type="button" style={styles.immBtnPrimary} onClick={runVerifyStep} disabled={immVerifyBusy}>
                        {immVerifyBusy ? 'Retrying...' : 'Retry verify'}
                      </button>
                      <button type="button" style={styles.immBtnGhost} onClick={closeImmModal} disabled={immVerifyBusy}>
                        Close
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      style={{
                        ...styles.immBtnPrimary,
                        opacity: immVerifyDone ? 1 : 0.6,
                        cursor: immVerifyDone ? 'pointer' : 'not-allowed',
                      }}
                      disabled={!immVerifyDone}
                      onClick={() => setImmStep(4)}
                    >
                      Continue to mint
                    </button>
                  )}
                </div>
              </>
            )}

            {immStep === 4 && (
              <>
                <h2 id="immortalize-title" style={styles.immTitle}>
                  Minting on Polygon Amoy
                </h2>
                <p style={styles.immText}>
                  Review the public information below before your wallet asks you to approve the transaction.
                  <br />
                  <span style={{ color: WARM_GRAY, fontSize: 13 }}>
                    The IPFS image and metadata may remain publicly available even if you later stop using BWK.
                  </span>
                </p>

                <div style={styles.immDisclosureBox} aria-labelledby="mint-public-data-title">
                  <div id="mint-public-data-title" style={styles.immDisclosureTitle}>
                    Public and potentially permanent
                  </div>
                  <dl style={styles.immDisclosureList}>
                    {mintDisclosureItems.map((item) => (
                      <div key={item.label} style={styles.immDisclosureRow}>
                        <dt style={styles.immDisclosureLabel}>{item.label}</dt>
                        <dd style={styles.immDisclosureValue}>{item.value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>

                <div style={styles.immProgressBox}>
                  <div style={{ fontWeight: 900, marginBottom: 6 }}>
                    Wallet:{' '}
                    <span style={{ color: WARM_GRAY }}>
                      {walletAddress ? `${walletAddress.slice(0, 6)}…${walletAddress.slice(-4)}` : '<not connected>'}
                    </span>
                  </div>

                  {immUploadResult?.tokenUri ? (
                    <div style={{ fontSize: 13, marginTop: 6 }}>
                      <strong>Token URI:</strong> <code style={styles.immCode}>{immUploadResult.tokenUri}</code>
                    </div>
                  ) : null}

                  {immMintError ? (
                    <div style={styles.immErrorBox}>
                      <div style={{ fontWeight: 900 }}>Mint failed</div>
                      <div style={{ fontSize: 13 }}>{immMintError}</div>
                    </div>
                  ) : null}
                </div>

                <label style={styles.immDisclosureConfirm}>
                  <input
                    type="checkbox"
                    checked={immDisclosureAccepted}
                    onChange={(event) => {
                      setImmDisclosureAccepted(event.target.checked);
                      if (event.target.checked) setImmMintError('');
                    }}
                    disabled={immMintBusy}
                    style={styles.immDisclosureCheckbox}
                  />
                  <span>
                    I understand that this information will be public and may be permanent,
                    and I want to continue to wallet approval.
                  </span>
                </label>

                <div style={styles.immActions}>
                  {!immMintAttempted || !immMintError ? (
                    <button
                      type="button"
                      style={{
                        ...styles.immBtnPrimary,
                        opacity: immMintBusy || !immDisclosureAccepted ? 0.65 : 1,
                        cursor: immMintBusy || !immDisclosureAccepted ? 'not-allowed' : 'pointer',
                      }}
                      onClick={runMintStep}
                      disabled={immMintBusy || !immDisclosureAccepted}
                    >
                      {immMintBusy ? 'Opening wallet…' : 'Continue to wallet approval'}
                    </button>
                  ) : (
                    <>
                      <button type="button" style={styles.immBtnPrimary} onClick={runMintStep} disabled={immMintBusy || !immDisclosureAccepted}>
                        {immMintBusy ? 'Minting…' : 'Retry'}
                      </button>
                      <button type="button" style={styles.immBtnGhost} onClick={closeImmModal} disabled={immMintBusy}>
                        Cancel
                      </button>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Report modal */}
      {reportOpen && (
        <div
          style={styles.modalBackdrop}
          role="dialog"
          aria-modal="true"
          aria-labelledby="report-dialog-title"
          onClick={() => !reportBusy && setReportOpen(false)}
          onKeyDown={(e) => {
            if (e.key === 'Escape' && !reportBusy) setReportOpen(false);
          }}
        >
          <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <h2 id="report-dialog-title" style={{ margin: '0 0 8px', fontFamily: 'serif' }}>
              Report memory
            </h2>
            <p style={{ marginTop: 0, color: WARM_GRAY, fontSize: 14 }}>
              Tell us why you’re reporting this memory. Your report is private and will be reviewed by an admin.
            </p>
            <form onSubmit={submitReport} style={{ display: 'grid', gap: 10 }}>
              <fieldset style={styles.fs}>
                <legend style={styles.legend}>Reason</legend>
                <label style={styles.reasonRow}>
                  <input
                    ref={reportFirstControlRef}
                    type="radio"
                    name="reason"
                    value="spam"
                    checked={reason === 'spam'}
                    onChange={() => setReason('spam')}
                    autoFocus
                  />
                  <span>Spam / Scam</span>
                </label>
                <label style={styles.reasonRow}>
                  <input
                    type="radio"
                    name="reason"
                    value="inappropriate"
                    checked={reason === 'inappropriate'}
                    onChange={() => setReason('inappropriate')}
                  />
                  <span>Inappropriate / Abusive</span>
                </label>
                <label style={styles.reasonRow}>
                  <input type="radio" name="reason" value="privacy" checked={reason === 'privacy'} onChange={() => setReason('privacy')} />
                  <span>Privacy concern</span>
                </label>
                <label style={styles.reasonRow}>
                  <input type="radio" name="reason" value="other" checked={reason === 'other'} onChange={() => setReason('other')} />
                  <span>Other</span>
                </label>
              </fieldset>

              <label style={{ fontWeight: 800, fontSize: 13 }}>Note (optional)</label>
              <textarea
                rows={4}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Add details for the reviewers…"
                style={styles.textarea}
              />

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setReportOpen(false)}
                  disabled={reportBusy}
                  style={styles.ghostBtn}
                  ref={closeBtnRef}
                  aria-label="Cancel report"
                >
                  Cancel
                </button>
                <button type="submit" disabled={reportBusy} style={styles.copyBtn}>
                  {reportBusy ? 'Sending…' : 'Send Report'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { maxWidth: 900, margin: '0 auto', padding: '20px 16px 24px', color: IVORY },
  navRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  back: { textDecoration: 'none', fontWeight: 800, color: GOLD, padding: '6px 0' },
  title: { margin: '6px 0 12px', fontFamily: 'serif', fontSize: 28, color: IVORY },

  // Silver CTA
  specialRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
    marginBottom: 10,
  },
  walletLinkNotice: {
    maxWidth: 440,
    padding: '8px 10px',
    borderRadius: 10,
    border: '1px solid rgba(201,164,76,0.35)',
    background: 'rgba(201,164,76,0.10)',
    color: IVORY,
    fontSize: 12,
    lineHeight: 1.4,
  },
  silverButton: {
    padding: '10px 22px',
    borderRadius: 999,
    border: `1px solid ${GOLD}`,
    background: GOLD,
    boxShadow: '0 10px 24px rgba(10,26,51,0.28), inset 0 1px 0 rgba(253,252,249,0.35)',
    color: NAVY,
    fontWeight: 900,
    letterSpacing: 0.09,
    fontSize: 12,
    textTransform: 'uppercase',
    cursor: 'pointer',
    minWidth: 260,
    textAlign: 'center',
  },

  // Minted status UI
  mintedBox: {
    marginBottom: 12,
    padding: 12,
    borderRadius: 12,
    background: 'rgba(201,164,76,0.12)',
    border: '1px solid rgba(201,164,76,0.45)',
    color: NAVY,
  },
  mintedChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '3px 8px',
    borderRadius: 999,
    border: '1px solid rgba(10,26,51,0.18)',
    background: '#fff',
    fontSize: 12,
    fontWeight: 800,
    color: NAVY,
  },
  mintedLink: { fontSize: 13, color: ACCESSIBLE_GOLD, fontWeight: 900, textDecoration: 'none' },
  mintedRecoverBtn: {
    border: '1px solid rgba(10,26,51,0.20)',
    background: '#fff',
    color: NAVY,
    borderRadius: 10,
    padding: '7px 12px',
    fontWeight: 900,
    cursor: 'pointer',
  },

  card: {
    background: IVORY,
    color: NAVY,
    border: '1px solid rgba(201,164,76,0.35)',
    borderRadius: 14,
    padding: 16,
    boxShadow: '0 8px 28px rgba(10,26,51,0.25)',
  },
  metaRowRight: { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 6, color: WARM_GRAY, marginBottom: 2, fontWeight: 600 },
  metaRowRightSub: { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 6, color: '#6f7a8c', marginBottom: 6, fontWeight: 600, fontSize: 13 },
  heroImage: { width: 160, height: 160, objectFit: 'cover', borderRadius: 10, border: '1px solid rgba(201,164,76,0.25)', background: '#fff' },
  storyCard: { border: '1px solid rgba(201,164,76,0.35)', borderRadius: 12, padding: 14, marginTop: 4, background: '#fff' },
  storyLabel: { fontWeight: 800, fontSize: 14, marginBottom: 6 },
  storyText: { margin: 0, lineHeight: 1.6, whiteSpace: 'pre-wrap' },

  copyBtn: { background: GOLD, color: NAVY, border: 'none', borderRadius: 10, padding: '8px 12px', fontWeight: 900, cursor: 'pointer' },
  copyBtnSuccess: { background: EMERALD, color: IVORY },
  ghostBtn: { textDecoration: 'none', background: 'transparent', color: ACCESSIBLE_GOLD, border: `1px solid ${GOLD}`, borderRadius: 10, padding: '7px 12px', fontWeight: 900 },

  reportBtn: { background: 'transparent', color: BURGUNDY, border: `1px solid ${ROSE_CLAY}`, borderRadius: 10, padding: '7px 12px', fontWeight: 900 },

  exportHidden: {
    position: 'absolute',
    left: -20000,
    top: 0,
    width: 1080,
    height: 1080,
    overflow: 'hidden',
    pointerEvents: 'none',
  },
  exportFrame: {
    width: '100%',
    height: '100%',
    background: IVORY,
    color: NAVY,
    borderRadius: 24,
    border: `2px solid ${GOLD}`,
    padding: 32,
    boxSizing: 'border-box',
    boxShadow: 'none',
    fontFamily: 'system-ui, -apple-system, Segoe UI, Inter, Roboto, sans-serif',
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    position: 'relative',
  },
  exportHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' },
  exportBrand: { fontWeight: 900, letterSpacing: 0.4 },
  exportSlogan: { color: WARM_GRAY, fontStyle: 'italic' },
  exportSquareBody: {
    flex: 1,
    minHeight: 0,
    position: 'relative',
    background: IVORY,
  },
  exportSquareTitle: {
    fontFamily: 'serif',
    fontSize: 52,
    lineHeight: 1.1,
    color: NAVY,
    margin: 0,
    maxWidth: 780,
    position: 'absolute',
    left: 18,
    top: 18,
  },
  exportSquareHeroRow: {
    position: 'absolute',
    left: 18,
    right: 0,
    top: 180,
    display: 'grid',
    gridTemplateColumns: '360px 1fr',
    gap: 34,
    alignItems: 'start',
  },
  exportSquareImageCol: {
    width: 360,
    height: 360,
    display: 'grid',
    placeItems: 'center',
  },
  exportSquareImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    borderRadius: 20,
    background: '#fff',
  },
  exportSquareImagePlaceholder: {
    width: '100%',
    height: '100%',
    borderRadius: 18,
    border: '1px dashed rgba(10,26,51,0.18)',
    display: 'grid',
    placeItems: 'center',
    color: WARM_GRAY,
    background: '#fff',
    fontSize: 18,
    fontWeight: 700,
  },
  exportSquareMetaCol: {
    minWidth: 0,
    display: 'grid',
    justifyItems: 'end',
    alignContent: 'start',
    gap: 10,
    paddingTop: 14,
    color: '#6f7a8c',
    fontSize: 16,
    fontWeight: 600,
    paddingRight: 8,
  },
  exportSquareTime: {
    color: WARM_GRAY,
    fontSize: 16,
    textAlign: 'right',
  },
  exportSquareDate: {
    color: WARM_GRAY,
    fontSize: 18,
    textAlign: 'right',
  },
  exportSquareLocation: {
    color: '#6f7a8c',
    fontSize: 17,
    textAlign: 'right',
    fontWeight: 600,
  },
  exportSquareSpacer: {
    display: 'none',
  },
  exportSquareStory: {
    position: 'absolute',
    left: 18,
    right: 18,
    bottom: 74,
    border: '1px solid rgba(201,164,76,0.35)',
    borderRadius: 16,
    padding: 18,
    background: '#fff',
    minHeight: 112,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  exportSquareStoryLabel: {
    fontWeight: 800,
    fontSize: 14,
    marginBottom: 14,
  },
  exportSquareStoryText: {
    margin: 0,
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
    fontSize: 20,
    color: NAVY,
    overflow: 'visible',
  },
  exportSquareFooter: {
    position: 'absolute',
    right: 12,
    bottom: 10,
    color: WARM_GRAY,
    fontSize: 14,
    textAlign: 'right',
    fontWeight: 600,
  },
  exportCanvasBody: { display: 'none' },
  exportCanvasMainColumn: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
  },
  exportCanvasSideColumn: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
  },
  exportCanvasTitleBlock: {
    minHeight: 160,
    paddingTop: 8,
  },
  exportCanvasTitle: {
    fontFamily: 'serif',
    fontSize: 52,
    lineHeight: 1.08,
    margin: 0,
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  exportCanvasMetaCard: {
    border: '1px solid rgba(201,164,76,0.35)',
    borderRadius: 16,
    background: '#fff',
    padding: '18px 20px',
    display: 'grid',
    gap: 10,
  },
  exportCanvasDate: {
    color: WARM_GRAY,
    fontSize: 20,
    textAlign: 'left',
    fontWeight: 700,
  },
  exportCanvasLocation: {
    color: '#6f7a8c',
    fontSize: 18,
    textAlign: 'left',
    fontWeight: 700,
    lineHeight: 1.35,
  },
  exportCanvasImageShell: {
    flex: 1,
    minHeight: 0,
    display: 'grid',
    placeItems: 'center',
    borderRadius: 20,
    border: '1px solid rgba(201,164,76,0.35)',
    background: 'linear-gradient(180deg, rgba(201,164,76,0.12) 0%, rgba(201,164,76,0.03) 100%)',
    padding: 18,
  },
  exportCanvasImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    borderRadius: 16,
    border: '1px solid rgba(201,164,76,0.28)',
    background: '#fff',
  },
  exportCanvasImagePlaceholder: {
    width: '100%',
    height: '100%',
    borderRadius: 16,
    border: '1px dashed rgba(10,26,51,0.18)',
    background: 'rgba(255,255,255,0.7)',
    display: 'grid',
    placeItems: 'center',
    color: WARM_GRAY,
    fontSize: 22,
    fontWeight: 700,
  },
  exportCanvasStory: {
    flex: 1,
    minHeight: 0,
    border: '1px solid rgba(201,164,76,0.35)',
    borderRadius: 18,
    padding: 22,
    background: '#fff',
    display: 'flex',
    flexDirection: 'column',
  },
  exportCanvasStoryLabel: {
    fontWeight: 800,
    marginBottom: 10,
    fontSize: 18,
  },
  exportCanvasStoryText: {
    whiteSpace: 'pre-wrap',
    lineHeight: 1.65,
    fontSize: 24,
    display: '-webkit-box',
    WebkitLineClamp: 12,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  exportCanvasStoryPlaceholder: {
    flex: 1,
    minHeight: 0,
    border: '1px dashed rgba(10,26,51,0.18)',
    borderRadius: 18,
    background: 'rgba(255,255,255,0.8)',
    color: WARM_GRAY,
    fontSize: 28,
    fontStyle: 'italic',
    display: 'grid',
    placeItems: 'center',
    padding: 22,
    textAlign: 'center',
  },
  exportTitle: { display: 'none' },
  exportDate: { display: 'none' },
  exportLocation: { display: 'none' },
  exportImage: { display: 'none' },
  exportStory: { display: 'none' },
  exportStoryLabel: { display: 'none' },
  exportStoryText: { display: 'none' },
  exportFooter: { display: 'none' },

  sheetBackdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 },
  sheet: { width: 360, maxWidth: '90vw', background: IVORY, color: NAVY, borderRadius: 16, border: '1px solid rgba(201,164,76,0.35)', boxShadow: '0 20px 60px rgba(201,164,76,0.35)', padding: 16 },
  sheetTitle: { fontWeight: 900, marginBottom: 12 },
  sheetRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 },
  sheetBtn: { padding: '10px 12px', borderRadius: 10, border: '1px solid rgba(10,26,51,0.2)', background: '#fff', cursor: 'pointer', fontWeight: 700 },
  sheetCopy: { background: GOLD, color: NAVY, borderColor: GOLD },
  sheetClose: { marginTop: 10, width: '100%', padding: '10px 12px', borderRadius: 10, border: 'none', background: WARM_GRAY, color: 'white', fontWeight: 800, cursor: 'pointer' },

  // Modal styles
  modalBackdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'grid', placeItems: 'center', zIndex: 10000, padding: 12 },
  modalCard: { width: 'min(520px, 92vw)', background: IVORY, color: NAVY, borderRadius: 14, border: '1px solid rgba(201,164,76,0.35)', boxShadow: '0 16px 48px rgba(10,26,51,0.35)', padding: 14 },
  fs: { border: '1px solid rgba(10,26,51,0.12)', borderRadius: 10, padding: 10 },
  legend: { fontWeight: 800, fontSize: 13, color: WARM_GRAY },
  reasonRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' },
  textarea: { width: '100%', padding: 10, borderRadius: 10, border: '1px solid rgba(10,26,51,0.15)', background: '#fff', color: NAVY, outline: 'none', fontSize: 14 },

  // Map debug styles

  // Immortalize wizard styles
  immCard: { width: 'min(560px, 94vw)', maxHeight: 'calc(100dvh - 24px)', overflowY: 'auto', background: IVORY, color: NAVY, borderRadius: 16, border: '1px solid rgba(201,164,76,0.40)', boxShadow: '0 22px 70px rgba(10,26,51,0.45)', padding: 16 },
  immTopRow: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, marginBottom: 10 },
  immPill: { fontSize: 12, fontWeight: 900, color: NAVY, background: 'rgba(201,164,76,0.25)', border: '1px solid rgba(201,164,76,0.55)', padding: '5px 10px', borderRadius: 999 },
  immChecklist: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
    padding: '6px 10px',
    borderRadius: 12,
    border: '1px solid rgba(10,26,51,0.10)',
    background: 'rgba(10,26,51,0.03)',
  },
  immCloseBtn: { border: '1px solid rgba(10,26,51,0.18)', background: '#fff', borderRadius: 10, width: 36, height: 36, cursor: 'pointer', fontWeight: 900, color: NAVY },
  immTitle: { margin: '2px 0 8px', fontFamily: 'serif', fontSize: 22 },
  immText: { margin: '0 0 12px', color: WARM_GRAY, lineHeight: 1.55 },
  immInfoBox: { borderRadius: 12, border: '1px solid rgba(10,26,51,0.12)', background: '#fff', padding: 12, marginBottom: 12 },
  immProgressBox: { borderRadius: 12, border: '1px solid rgba(10,26,51,0.12)', background: '#fff', padding: 12, marginBottom: 12 },
  immDisclosureBox: { borderRadius: 12, border: '1px solid rgba(138,109,46,0.38)', background: 'rgba(201,164,76,0.09)', padding: 12, marginBottom: 12 },
  immDisclosureTitle: { color: NAVY, fontWeight: 900, marginBottom: 8 },
  immDisclosureList: { display: 'grid', gap: 0, margin: 0 },
  immDisclosureRow: { display: 'grid', gridTemplateColumns: 'minmax(112px, 0.7fr) minmax(0, 1.3fr)', gap: 10, padding: '7px 0', borderTop: '1px solid rgba(10,26,51,0.10)' },
  immDisclosureLabel: { color: NAVY, fontSize: 12, fontWeight: 900 },
  immDisclosureValue: { minWidth: 0, margin: 0, color: '#4B5563', fontSize: 12, overflowWrap: 'anywhere' },
  immDisclosureConfirm: { minHeight: 44, display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', marginBottom: 12, borderRadius: 12, border: '1px solid rgba(15,118,110,0.35)', background: 'rgba(15,118,110,0.08)', color: NAVY, fontSize: 13, lineHeight: 1.5, cursor: 'pointer' },
  immDisclosureCheckbox: { width: 20, height: 20, margin: '1px 0 0', flex: '0 0 auto', accentColor: TEAL },
  immErrorBox: { marginTop: 10, borderRadius: 12, border: '1px solid rgba(183,110,121,0.55)', background: 'rgba(92,26,43,0.08)', color: BURGUNDY, padding: 10 },
  immCode: {
    display: 'inline-block',
    maxWidth: '100%',
    marginLeft: 6,
    padding: '2px 6px',
    borderRadius: 6,
    background: 'rgba(10,26,51,0.06)',
    border: '1px solid rgba(10,26,51,0.12)',
    fontSize: 12,
    fontFamily: 'monospace',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
    verticalAlign: 'top',
    lineHeight: 1.45,
    boxSizing: 'border-box',
  },
  immActions: { display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' },
  immBtnPrimary: { background: GOLD, color: NAVY, border: 'none', borderRadius: 12, padding: '10px 14px', fontWeight: 900, cursor: 'pointer', minWidth: 140 },
  immBtnGhost: { background: 'transparent', color: NAVY, border: '1px solid rgba(10,26,51,0.20)', borderRadius: 12, padding: '10px 14px', fontWeight: 900, cursor: 'pointer', minWidth: 110 },
};
// ==========================
// End Version 43 — MemoryDetails.js
// ==========================
